/**
 * Aggregation Service
 * 
 * Core service for incremental pre-computed aggregations.
 * Updates aggregations instantly on level completion (0ms delay).
 * All UI components read from these pre-computed aggregations.
 * Syncs to Firestore for persistence across browsers.
 */

import type { GameplayMode } from '../types/gamification';
import type {
  AggregatedDigraphs,
  AggregatedKeyMetrics,
  Aggregation,
  AggregationType,
  FilterValues,
  LevelCompletion,
} from '../types/levelCompletion';
import { firestoreService } from './database/FirestoreService';
import { indexedDBService } from './database/IndexedDBService';

// Legacy localStorage prefix kept ONLY for one-time migration of pre-IDB caches
// at hydrate() time. New writes go to IDB (durable) + the in-memory cache
// (synchronous reads). Once a browser has run hydrate once, no `agg_*` keys
// remain in localStorage.
const LEGACY_STORAGE_KEY_PREFIX = 'agg_';
function cacheKey(userId: string, aggId: string): string {
  return `${userId}_${aggId}`;
}

/**
 * Generate aggregation ID from type and filter values
 */
function generateAggId(type: AggregationType, filterValues: FilterValues): string {
  if (type === 'global') return 'global_all';
  if (type === 'daily' && filterValues.date) return `daily_${filterValues.date}`;
  if (type === 'keyboard' && filterValues.keyboardId) return `keyboard_${filterValues.keyboardId}`;
  if (type === 'language' && filterValues.language) return `language_${filterValues.language}`;
  if (type === 'mode' && filterValues.mode) {
    // For just_play mode with error mode, create mode_just_play_errorMode
    if (filterValues.mode === 'just_play' && filterValues.errorMode) {
      return `mode_just_play_${filterValues.errorMode}`;
    }
    return `mode_${filterValues.mode}`;
  }
  if (type === 'session' && filterValues.language && filterValues.keyboardId) {
    // For just_play sessions, include error mode if present
    if (filterValues.errorMode) {
      return `session_justplay_${filterValues.language}_${filterValues.errorMode}`;
    }
    return `session_${filterValues.language}_${filterValues.keyboardId}`;
  }
  if (type === 'combined') {
    const parts = [];
    
    // Check if this is a daily combination (date is present)
    if (filterValues.date) {
      parts.push('daily', filterValues.date);
    }
    
    // Check if this is an errorMode-only filter
    if (filterValues.errorMode && !filterValues.mode && !filterValues.language && !filterValues.keyboardId && !filterValues.date) {
      return `combined_errorMode_${filterValues.errorMode}`;
    }
    
    // Add other filters in consistent order
    if (filterValues.mode) parts.push(filterValues.mode);
    if (filterValues.language) parts.push(filterValues.language);
    if (filterValues.keyboardId) parts.push(filterValues.keyboardId);
    if (filterValues.errorMode) parts.push(filterValues.errorMode);
    
    return `combined_${parts.join('_')}`;
  }
  throw new Error(`Cannot generate aggId for type ${type} with filterValues ${JSON.stringify(filterValues)}`);
}

/**
 * Get today's date string in YYYY-MM-DD format (LOCAL TIMEZONE)
 * IMPORTANT: Uses local timezone to match user's perception of "today"
 */
function getTodayDateString(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

class AggregationServiceClass {
  // Batch sync queue for efficient Firestore writes
  private syncQueue = new Set<string>(); // aggIds pending sync
  private syncTimer: NodeJS.Timeout | null = null;
  private readonly SYNC_DEBOUNCE_MS = 5000; // 5 seconds
  private currentUserId: string | null = null;

  // Live-aggregation flush trigger: count level completions since the last
  // Firestore flush, fire one batched flush every LEVELS_PER_FLUSH levels.
  // Keeps writes bounded for free-tier scaling (see plan in floofy-sun).
  private levelsSinceFlush = new Map<string, number>();
  private readonly LEVELS_PER_FLUSH = 3;
  private liveFlushInFlight = new Set<string>();

  // In-memory cache (synchronous read tier). Hydrated from IDB on login.
  // Key shape: `${userId}_${aggId}`.
  private cache = new Map<string, Aggregation>();
  private hydrated = new Set<string>();
  private hydrationPromises = new Map<string, Promise<void>>();

  /**
   * Load all aggregations for a user from IDB into the in-memory cache, then
   * migrate any leftover legacy `agg_*` localStorage keys (one-time, idempotent).
   * Must be awaited by AuthContext before unblocking the UI — synchronous
   * `getAggregation` callers depend on the cache being warm.
   */
  async hydrate(userId: string): Promise<void> {
    if (this.hydrated.has(userId)) return;
    const inFlight = this.hydrationPromises.get(userId);
    if (inFlight) return inFlight;

    const promise = (async () => {
      try {
        const rows = await indexedDBService.getAggregationsForUser(userId);
        for (const row of rows) {
          const agg = this.reviveAggregation(row.data);
          this.cache.set(cacheKey(userId, row.aggId), agg);
        }
        if (rows.length > 0) {
          console.log(`💧 Hydrated ${rows.length} aggregations from IDB for ${userId}`);
        }

        await this.migrateLegacyLocalStorage(userId);
        this.hydrated.add(userId);
      } finally {
        this.hydrationPromises.delete(userId);
      }
    })();

    this.hydrationPromises.set(userId, promise);
    return promise;
  }

  private async migrateLegacyLocalStorage(userId: string): Promise<void> {
    const prefix = `${LEGACY_STORAGE_KEY_PREFIX}${userId}_`;
    const candidates: Array<{ lsKey: string; aggId: string }> = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix)) {
          candidates.push({ lsKey: k, aggId: k.slice(prefix.length) });
        }
      }
    } catch {
      return;
    }
    if (candidates.length === 0) return;

    let migrated = 0;
    for (const { lsKey, aggId } of candidates) {
      const ckey = cacheKey(userId, aggId);
      // Already in IDB (loaded during hydrate above) — just drop the legacy copy.
      if (this.cache.has(ckey)) {
        try { localStorage.removeItem(lsKey); } catch {}
        continue;
      }
      try {
        const raw = localStorage.getItem(lsKey);
        if (!raw) { localStorage.removeItem(lsKey); continue; }
        const agg = this.reviveAggregation(JSON.parse(raw));
        await indexedDBService.putAggregation(userId, aggId, agg);
        this.cache.set(ckey, agg);
        localStorage.removeItem(lsKey);
        migrated++;
      } catch (err) {
        console.warn(`Could not migrate legacy aggregation ${lsKey}:`, err);
      }
    }
    if (migrated > 0) {
      console.log(`🔁 Migrated ${migrated} legacy localStorage aggregation(s) → IDB for ${userId}`);
    }
  }

  /**
   * Apply field migrations + date revival to a raw aggregation payload.
   * Idempotent. Run once at hydration / Firestore-pull time so reads are cheap.
   */
  private reviveAggregation(raw: any): Aggregation {
    const agg = raw;

    if (agg.lastUpdated && typeof agg.lastUpdated === 'string') {
      agg.lastUpdated = new Date(agg.lastUpdated);
    }
    if (agg.localTimestamp && typeof agg.localTimestamp === 'string') {
      agg.localTimestamp = new Date(agg.localTimestamp);
    }

    // MIGRATION 1: legacy ms→sec for totalTime (avg per level > 1000s is impossible)
    if (agg.totalLevels > 0 && agg.totalTime > 0) {
      const avgTimePerLevel = agg.totalTime / agg.totalLevels;
      if (avgTimePerLevel > 1000) {
        agg.totalTime = Math.floor(agg.totalTime / 1000);
      }
    }

    // MIGRATION 2: legacy character-key keyMetrics → charCode-key
    if (agg.keyMetrics) {
      const firstKey = Object.keys(agg.keyMetrics)[0];
      if (firstKey && firstKey.length <= 2 && isNaN(parseInt(firstKey))) {
        const migratedKeyMetrics: Record<string, any> = {};
        for (const [k, v] of Object.entries(agg.keyMetrics)) {
          migratedKeyMetrics[k.charCodeAt(0).toString()] = v;
        }
        agg.keyMetrics = migratedKeyMetrics;
      }
    }

    // Session field invariants
    if (agg.type === 'session') {
      if (!agg.completedLevels) agg.completedLevels = [];
      agg.completedSections = [];
    }

    return agg;
  }

  /**
   * No-op shim. Quota cleanup was localStorage-specific and is unnecessary
   * now that aggregations live in IndexedDB. Kept as an exported method so
   * existing callers (LevelCompletionService) compile without changes; it
   * just logs and returns. Remove once the call sites are cleaned up.
   * @deprecated
   */
  public checkQuotaAndCleanup(_userId: string, _force = false): void {
    // Aggregations are no longer stored in localStorage, so there is nothing
    // for this method to clean up. The Phase 0 boot-time pre-flight in
    // firebase.ts still drops any leftover legacy keys.
  }

  /**
   * Get aggregation from the in-memory cache (synchronous, 0ms).
   * Cache must be hydrated first via `hydrate(userId)` (called by AuthContext
   * before unblocking the UI). Field migrations + date revival happen at
   * hydrate time, so reads are pure lookups.
   */
  getAggregation(userId: string, aggId: string): Aggregation | null {
    const agg = this.cache.get(cacheKey(userId, aggId));
    if (!agg) return null;
    if ((agg as any).deleted === true) return null;
    return agg;
  }
  
  /**
   * Get or create aggregation (lazy initialization)
   */
  getOrCreateAggregation(userId: string, type: AggregationType, filterValues: FilterValues): Aggregation {
    const aggId = generateAggId(type, filterValues);
    const existing = this.getAggregation(userId, aggId);
    
    if (existing) {
      return existing;
    }
    
    // Create new aggregation
    const now = new Date();
    const newAgg: Aggregation = {
      id: aggId,
      type,
      filterValues,
      lastUpdated: now,
      localTimestamp: now,
      
      totalLevels: 0,
      totalKeystrokes: 0,
      totalErrors: 0,
      totalTime: 0,
      
      avgWPM: 0,
      avgTPM: 0,
      avgAccuracy: 0,
      
      bestWPM: 0,
      bestTPM: 0,
      bestAccuracy: 0,
      
      keyMetrics: {},
      digraphs: {},
      topMistakes: [],
    };
    
    // Add completedLevels for session aggregations
    if (type === 'session') {
      newAgg.completedLevels = [];
      newAgg.completedSections = [];
    }
    
    console.log(`✨ Created new aggregation: ${aggId}`);
    return newAgg;
  }
  
  /**
   * Save aggregation to in-memory cache and write through to IndexedDB.
   * IDB write is fire-and-forget — Firestore is the durable backstop, so a
   * failure here is a local cache miss, not data loss. No quota handling
   * needed: IDB allows hundreds of MB per origin.
   */
  public saveAggregation(userId: string, agg: Aggregation): void {
    this.cache.set(cacheKey(userId, agg.id), agg);
    indexedDBService.putAggregation(userId, agg.id, agg).catch(err => {
      console.warn(`IDB write failed for aggregation ${agg.id}:`, err);
    });
  }
  
  /**
   * Mark aggregation as deleted (soft delete)
   */
  async markAggregationAsDeleted(userId: string, aggId: string): Promise<void> {
    try {
      const agg = this.getAggregation(userId, aggId);
      
      if (!agg) {
        console.log(`📊 Aggregation ${aggId} not found, nothing to delete`);
        return;
      }
      
      // Mark as deleted in localStorage
      (agg as any).deleted = true;
      agg.lastUpdated = new Date();
      this.saveAggregation(userId, agg);
      
      // Mark as deleted in Firestore (if exists)
      try {
        const { firestoreService } = await import('./database/FirestoreService');
        await firestoreService.markAggregationAsDeleted(userId, aggId);
        console.log(`☁️ Marked aggregation ${aggId} as deleted in Firestore`);
      } catch (error) {
        // It's OK if Firestore document doesn't exist yet
        console.log(`⚠️ Could not mark aggregation ${aggId} as deleted in Firestore (may not exist yet):`, error);
      }
      
      console.log(`✅ Marked aggregation ${aggId} as deleted locally`);
    } catch (error) {
      console.error(`Error marking aggregation ${aggId} as deleted:`, error);
      throw error;
    }
  }
  
  /**
   * Mark all aggregations for a user as deleted (used in "Reset All Data")
   */
  async markAllAggregationsAsDeleted(userId: string): Promise<void> {
    try {
      await this.hydrate(userId);
      const prefix = `${userId}_`;
      for (const [ckey, agg] of this.cache) {
        if (!ckey.startsWith(prefix)) continue;
        (agg as any).deleted = true;
        agg.lastUpdated = new Date();
        this.saveAggregation(userId, agg);
      }
      console.log(`✅ Marked all aggregations for user ${userId} as deleted locally`);
      
      // Also mark in Firestore (if exists)
      try {
        const { firestoreService } = await import('./database/FirestoreService');
        await firestoreService.markAllAggregationsAsDeleted(userId);
      } catch (error) {
        console.warn('⚠️ Could not mark all aggregations as deleted in Firestore:', error);
      }
    } catch (error) {
      console.error('Error marking all aggregations as deleted:', error);
      throw error;
    }
  }
  
  /**
   * Update weighted average
   */
  private updateWeightedAverage(oldAvg: number, oldCount: number, newValue: number): number {
    if (oldCount === 0) return newValue;
    return (oldAvg * oldCount + newValue) / (oldCount + 1);
  }
  
  /**
   * Merge key metrics from level into aggregation
   */
  private mergeKeyMetrics(
    aggMetrics: AggregatedKeyMetrics,
    levelMetrics: Record<string, any>
  ): void {
    for (const [key, stats] of Object.entries(levelMetrics)) {
      
      if (!aggMetrics[key]) {
        aggMetrics[key] = {
          totalAttempts: 0,
          totalErrors: 0,
          totalTime: 0,
          totalReliableCount: 0,
          avgSpeed: 0,
          errorRate: 0,
        };
      }

      aggMetrics[key].totalAttempts += stats.attempts || 0;
      aggMetrics[key].totalErrors += stats.errors || 0;
      aggMetrics[key].totalTime += stats.totalTime || 0;
      // For levels written before reliableCount existed, fall back to attempts so
      // legacy data isn't treated as zero-reliable (which would zero the avgSpeed).
      const reliable = stats.reliableCount ?? stats.attempts ?? 0;
      aggMetrics[key].totalReliableCount = (aggMetrics[key].totalReliableCount ?? 0) + reliable;

      // Compute derived fields
      if (aggMetrics[key].totalAttempts > 0) {
        const denom = aggMetrics[key].totalReliableCount || aggMetrics[key].totalAttempts;
        aggMetrics[key].avgSpeed = aggMetrics[key].totalTime / denom;
        aggMetrics[key].errorRate = aggMetrics[key].totalErrors / aggMetrics[key].totalAttempts;
      }
    }
  }
  
  /**
   * Merge digraphs from level into aggregation
   */
  private mergeDigraphs(
    aggDigraphs: AggregatedDigraphs,
    levelDigraphs: Record<string, any>
  ): void {
    for (const [digraph, stats] of Object.entries(levelDigraphs)) {
      if (!aggDigraphs[digraph]) {
        aggDigraphs[digraph] = {
          totalTime: 0,
          count: 0,
          avgTime: 0,
        };
      }
      
      aggDigraphs[digraph].totalTime += stats.totalTime || 0;
      aggDigraphs[digraph].count += stats.count || 0;
      
      // Compute derived field
      if (aggDigraphs[digraph].count > 0) {
        aggDigraphs[digraph].avgTime = aggDigraphs[digraph].totalTime / aggDigraphs[digraph].count;
      }
    }
  }
  
  /**
   * Merge topMistakes from level's keyMetrics confusions into aggregation
   */
  private mergeTopMistakes(
    agg: Aggregation,
    levelKeyMetrics: Record<string, any>
  ): void {
    // Initialize topMistakes if it doesn't exist
    if (!agg.topMistakes) {
      agg.topMistakes = [];
    }
    
    // Create a map for efficient merging
    const mistakesMap = new Map<string, { expected: string; typed: string; count: number }>();
    
    // Load existing mistakes into map
    agg.topMistakes.forEach(mistake => {
      const key = `${mistake.expected}→${mistake.typed}`;
      mistakesMap.set(key, mistake);
    });
    
    // Extract confusions from each key's metrics
    for (const [expectedKey, stats] of Object.entries(levelKeyMetrics)) {
      if (stats.confusions && Array.isArray(stats.confusions)) {
        stats.confusions.forEach((confusion: any) => {
          // Convert charCode keys back to characters for display
          const expectedChar = String.fromCharCode(parseInt(expectedKey));
          
          // Handle both old (charCode) and new (character) formats for typed
          let typedChar = confusion.typed;
          if (typedChar && /^\d+$/.test(typedChar) && parseInt(typedChar) < 128) {
            // It's a charCode string (e.g., "34" for quote), convert it
            typedChar = String.fromCharCode(parseInt(typedChar));
          }
          
          const key = `${expectedChar}→${typedChar}`;
          const existing = mistakesMap.get(key);
          
          if (existing) {
            existing.count += confusion.count || 0;
          } else {
            mistakesMap.set(key, {
              expected: expectedChar,
              typed: typedChar,
              count: confusion.count || 0,
            });
          }
        });
      }
    }
    
    // Convert map back to sorted array (top 50)
    agg.topMistakes = Array.from(mistakesMap.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 50);
  }
  
  /**
   * Update aggregation with new level completion (incremental)
   */
  updateAggregation(
    userId: string,
    aggId: string,
    level: LevelCompletion
  ): Aggregation {
    
    // Load or create aggregation (getAggregation returns null for deleted aggs)
    let agg = this.getAggregation(userId, aggId);
    if (!agg) {
      // Derive type and filters from aggId for lazy creation
      const type = this.deriveTypeFromAggId(aggId);
      const filterValues = this.deriveFiltersFromAggId(aggId, level);
      agg = this.getOrCreateAggregation(userId, type, filterValues);
    }
    
    // CRITICAL FIX: If aggregation was marked as deleted, the getAggregation above
    // returns null, so we create a fresh one. This ensures old deleted data is not carried forward.
    
    const oldCount = agg.totalLevels;
    
    // Update counts
    // IMPORTANT: Increment for EVERY completion, including duplicate attempts
    // This ensures daily activity, streak tracking, and averages include all practice
    agg.totalLevels++;  // ← Counts ALL attempts (including repeats)
    agg.totalKeystrokes += level.sessionFingerprint.totalKeystrokes;
    agg.totalErrors += Object.values(level.keyMetrics).reduce((sum, key) => sum + key.errors, 0);
    agg.totalTime += level.timeSpent;
    
    // Update weighted averages
    agg.avgWPM = this.updateWeightedAverage(agg.avgWPM, oldCount, level.wpm);
    agg.avgTPM = this.updateWeightedAverage(agg.avgTPM, oldCount, level.tpm);
    agg.avgAccuracy = this.updateWeightedAverage(agg.avgAccuracy, oldCount, level.accuracy);
    
    // Update best scores
    agg.bestWPM = Math.max(agg.bestWPM, level.wpm);
    agg.bestTPM = Math.max(agg.bestTPM, level.tpm);
    agg.bestAccuracy = Math.max(agg.bestAccuracy, level.accuracy);
    
    // Merge key metrics
    this.mergeKeyMetrics(agg.keyMetrics, level.keyMetrics);
    
    // Merge digraphs
    this.mergeDigraphs(agg.digraphs, level.digraphs);
    
    // Merge topMistakes from level's keyMetrics confusions
    this.mergeTopMistakes(agg, level.keyMetrics);
    
    // Add to completed levels (for session aggregations)
    if (agg.type === 'session') {
      // Ensure arrays exist (backward compatibility)
      if (!agg.completedLevels) {
        console.log(`🔧 Initializing completedLevels for ${aggId}`);
        agg.completedLevels = [];
      }
      if (!agg.completedSections) {
        agg.completedSections = [];
      }
      
      // Add level if not already present
      if (!agg.completedLevels.includes(level.levelId)) {
        agg.completedLevels.push(level.levelId);
      }
      
      // NOTE: We do NOT mark sections as complete here because we don't have
      // the full section structure to verify that ALL levels in the section are done.
      // The SessionCard will determine the next incomplete section by checking
      // individual level completion status.
    }
    
    // Update dailyBreakdown (for non-daily aggregations)
    if (agg.type !== 'daily') {
      // Convert timestamp to local date string to match getTodayDateString()
      const dateObj = level.timestamp instanceof Date ? level.timestamp : new Date(level.timestamp);
      const levelDate = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
      
      console.log(`  📅 Adding to dailyBreakdown for date: ${levelDate}`);
      
      if (!agg.dailyBreakdown) {
        agg.dailyBreakdown = [];
      }
      
      // Find or create entry for this date
      let dayEntry = agg.dailyBreakdown.find(d => d.date === levelDate);
      if (!dayEntry) {
        dayEntry = {
          date: levelDate,
          levelCount: 0,
          sessions: 1,
          avgWpm: 0,
          avgTpm: 0,
          avgAccuracy: 0,
        };
        agg.dailyBreakdown.push(dayEntry);
      }
      
      // Update day entry with weighted averages
      const oldDayCount = dayEntry.levelCount;
      dayEntry.levelCount++;  // ← Counts ALL attempts (user can repeat levels)
      dayEntry.avgWpm = this.updateWeightedAverage(dayEntry.avgWpm || 0, oldDayCount, level.wpm);
      dayEntry.avgTpm = this.updateWeightedAverage(dayEntry.avgTpm || 0, oldDayCount, level.tpm);
      dayEntry.avgAccuracy = this.updateWeightedAverage(dayEntry.avgAccuracy || 0, oldDayCount, level.accuracy);
    }
    
    // Update timestamps
    agg.lastUpdated = new Date();
    agg.localTimestamp = new Date();
    
    // Save to localStorage (instant)
    this.saveAggregation(userId, agg);
    
    // NOTE: Real-time Firestore sync removed for optimization
    // Aggregations now sync only on daily job or logout
    // This reduces Firestore writes by 95%
    
    console.log(`✅ Updated aggregation ${aggId}: ${agg.totalLevels} levels`);
    return agg;
  }
  
  /**
   * Derive aggregation type from aggId
   */
  private deriveTypeFromAggId(aggId: string): AggregationType {
    if (aggId.startsWith('global_')) return 'global';
    if (aggId.startsWith('daily_')) return 'daily';
    if (aggId.startsWith('keyboard_')) return 'keyboard';
    if (aggId.startsWith('language_')) return 'language';
    if (aggId.startsWith('mode_')) return 'mode';
    if (aggId.startsWith('session_')) return 'session';
    if (aggId.startsWith('combined_')) return 'combined';
    throw new Error(`Cannot derive type from aggId: ${aggId}`);
  }
  
  /**
   * Derive filter values from aggId and level data
   */
  private deriveFiltersFromAggId(aggId: string, level: LevelCompletion): FilterValues {
    if (aggId === 'global_all') return {};
    
    if (aggId.startsWith('daily_')) {
      const date = aggId.replace('daily_', '');
      return { date };
    }
    
    if (aggId.startsWith('keyboard_')) {
      return { keyboardId: level.keyboardId };
    }
    
    if (aggId.startsWith('language_')) {
      return { language: level.language };
    }
    
    if (aggId.startsWith('mode_')) {
      // Check if this is mode_just_play_errorMode (legacy format)
      if (aggId.startsWith('mode_just_play_')) {
        const errorMode = aggId.replace('mode_just_play_', '') as 'strict' | 'pass' | 'hold';
        return { mode: 'just_play' as const, errorMode };
      }
      return { mode: level.mode };
    }
    
    if (aggId.startsWith('session_')) {
      // For Just Play sessions: session_justplay_{category}_{keyboardId}
      if (aggId.includes('justplay')) {
        return { 
          mode: 'just_play' as const,
          language: level.language,
          keyboardId: level.keyboardId,
          errorMode: level.errorMode,
        };
      }
      // For course sessions: session_{language}_{keyboardId}
      // NOTE: Don't include errorMode in session filterValues - sessions track progress
      // across all error modes for a language+keyboard combination
      return { 
        mode: 'courses' as const,
        language: level.language, 
        keyboardId: level.keyboardId,
      };
    }
    
    if (aggId.startsWith('combined_')) {
      // Parse combined aggregation ID
      // Format can be: 
      // - combined_mode_language_keyboardId_errorMode
      // - combined_daily_YYYY-MM-DD_errorMode
      // - combined_errorMode_strict (single error mode filter)
      // - etc.
      const remainder = aggId.replace('combined_', '');
      const filters: FilterValues = {};
      
      // Check for error mode (strict, pass, hold) at the end
      const errorModes = ['strict', 'pass', 'hold'];
      for (const em of errorModes) {
        if (remainder.endsWith(`_${em}`)) {
          filters.errorMode = em as 'strict' | 'pass' | 'hold';
          break;
        }
      }
      
      // Check if this is a single errorMode filter (combined_errorMode_{mode})
      if (remainder.startsWith('errorMode_')) {
        return filters; // Only errorMode filter
      }
      
      // Check if remainder contains a date (daily_YYYY-MM-DD pattern)
      const dailyMatch = remainder.match(/daily_(\d{4}-\d{2}-\d{2})/);
      if (dailyMatch) {
        filters.date = dailyMatch[1];
        
        // Parse remaining parts after date
        let afterDate = remainder.substring(dailyMatch[0].length);
        if (afterDate.startsWith('_')) afterDate = afterDate.substring(1);
        if (filters.errorMode && afterDate.endsWith(`_${filters.errorMode}`)) {
          afterDate = afterDate.substring(0, afterDate.lastIndexOf(`_${filters.errorMode}`));
        }
        
        if (afterDate) {
          // Try to match mode, language, keyboard from the string BEFORE splitting
          // This handles multi-word modes like 'just_play'
          if (afterDate.includes(level.mode)) filters.mode = level.mode;
          if (afterDate.includes(level.language)) filters.language = level.language;
          if (afterDate.includes('kb_')) {
            const parts = afterDate.split('_');
            filters.keyboardId = parts.find(p => p.startsWith('kb_')) || level.keyboardId;
          }
        }
        
        return filters;
      }
      
      // Check if remainder contains a keyboard ID (starts with 'kb_')
      const kbIndex = remainder.indexOf('kb_');
      if (kbIndex !== -1) {
        // Extract keyboard ID (from 'kb_' to before any error mode)
        const kbPart = filters.errorMode 
          ? remainder.substring(kbIndex, remainder.lastIndexOf(`_${filters.errorMode}`))
          : remainder.substring(kbIndex);
        filters.keyboardId = kbPart;
        
        // Remove keyboard ID and error mode from remainder to parse mode/language
        const beforeKb = remainder.substring(0, kbIndex - 1); // -1 to remove trailing _
        
        // Try to match mode and language from the string BEFORE splitting
        // This handles multi-word modes like 'just_play'
        if (beforeKb.includes(level.mode)) filters.mode = level.mode;
        if (beforeKb.includes(level.language)) filters.language = level.language;
      } else {
        // No keyboard ID, just mode and/or language (and maybe error mode)
        let partsStr = remainder;
        if (filters.errorMode) {
          partsStr = remainder.substring(0, remainder.lastIndexOf(`_${filters.errorMode}`));
        }
        // Try to match mode and language from the string BEFORE splitting
        // This handles multi-word modes like 'just_play'
        if (partsStr.includes(level.mode)) filters.mode = level.mode;
        if (partsStr.includes(level.language)) filters.language = level.language;
      }
      
      return filters;
    }
    
    return {};
  }
  
  /**
   * Update all relevant aggregations after level completion
   * This is called immediately after a level is completed (local-first)
   * 
   * Creates ALL POSSIBLE COMBINATIONS of filters (4 dimensions):
   * - Single filters: mode, language, keyboard, errorMode (4 aggregations)
   * - Two-filter combinations: all pairs (6 aggregations)
   * - Three-filter combinations: all triplets (4 aggregations)
   * - Four-filter combination: mode+language+keyboard+errorMode (1 aggregation)
   * - Special: daily, session, global
   */
  // Track processed levels to prevent duplicate updates
  private processedLevels = new Set<string>();

  /**
   * Build the same set of aggregation IDs that updateAllRelevantAggregations
   * generates, but anchored to the LEVEL's date rather than today. Used by
   * rebuildAllAggregations so historical replays land on the correct day.
   * Skips aggIds whose required filter value is empty/missing on this level.
   */
  private buildAggIdsForLevel(level: LevelCompletion): string[] {
    const dateObj = level.timestamp instanceof Date ? level.timestamp : new Date(level.timestamp);
    const levelDate = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;

    const hasKeyboard = !!level.keyboardId;
    const hasLanguage = !!level.language;
    const hasMode = !!level.mode;
    const hasErrorMode = !!level.errorMode;

    const aggIds: string[] = [];

    aggIds.push(`daily_${levelDate}`);
    if (hasErrorMode) aggIds.push(`combined_daily_${levelDate}_${level.errorMode}`);

    if (hasMode) aggIds.push(`mode_${level.mode}`);
    if (hasLanguage) aggIds.push(`language_${level.language}`);
    if (hasKeyboard) aggIds.push(`keyboard_${level.keyboardId}`);

    if (hasErrorMode) {
      aggIds.push(`combined_errorMode_${level.errorMode}`);
      if (level.mode === 'just_play') aggIds.push(`mode_just_play_${level.errorMode}`);
    }

    if (hasMode && hasLanguage) aggIds.push(`combined_${level.mode}_${level.language}`);
    if (hasMode && hasKeyboard) aggIds.push(`combined_${level.mode}_${level.keyboardId}`);
    if (hasLanguage && hasKeyboard) aggIds.push(`combined_${level.language}_${level.keyboardId}`);

    if (hasMode) aggIds.push(`combined_daily_${levelDate}_${level.mode}`);
    if (hasLanguage) aggIds.push(`combined_daily_${levelDate}_${level.language}`);
    if (hasKeyboard) aggIds.push(`combined_daily_${levelDate}_${level.keyboardId}`);

    if (hasErrorMode) {
      if (hasMode) aggIds.push(`combined_${level.mode}_${level.errorMode}`);
      if (hasLanguage) aggIds.push(`combined_${level.language}_${level.errorMode}`);
      if (hasKeyboard) aggIds.push(`combined_${level.keyboardId}_${level.errorMode}`);
    }

    if (hasMode && hasLanguage && hasKeyboard) {
      aggIds.push(`combined_${level.mode}_${level.language}_${level.keyboardId}`);
    }
    if (hasMode && hasLanguage) aggIds.push(`combined_daily_${levelDate}_${level.mode}_${level.language}`);
    if (hasMode && hasKeyboard) aggIds.push(`combined_daily_${levelDate}_${level.mode}_${level.keyboardId}`);
    if (hasLanguage && hasKeyboard) aggIds.push(`combined_daily_${levelDate}_${level.language}_${level.keyboardId}`);

    if (hasErrorMode) {
      if (hasMode && hasLanguage) aggIds.push(`combined_${level.mode}_${level.language}_${level.errorMode}`);
      if (hasMode && hasKeyboard) aggIds.push(`combined_${level.mode}_${level.keyboardId}_${level.errorMode}`);
      if (hasLanguage && hasKeyboard) aggIds.push(`combined_${level.language}_${level.keyboardId}_${level.errorMode}`);
      if (hasMode) aggIds.push(`combined_daily_${levelDate}_${level.mode}_${level.errorMode}`);
      if (hasLanguage) aggIds.push(`combined_daily_${levelDate}_${level.language}_${level.errorMode}`);
      if (hasKeyboard) aggIds.push(`combined_daily_${levelDate}_${level.keyboardId}_${level.errorMode}`);

      if (hasMode && hasLanguage && hasKeyboard) {
        aggIds.push(`combined_${level.mode}_${level.language}_${level.keyboardId}_${level.errorMode}`);
        aggIds.push(`combined_daily_${levelDate}_${level.mode}_${level.language}_${level.errorMode}`);
      }
    }

    if (level.mode === 'just_play') {
      if (level.justPlayCategory && hasErrorMode && hasKeyboard) {
        aggIds.push(`session_justplay_${level.justPlayCategory}_${level.keyboardId}_${level.errorMode}`);
      }
    } else if (level.mode === 'courses' && hasLanguage && hasKeyboard) {
      aggIds.push(`session_${level.language}_${level.keyboardId}`);
    }

    aggIds.push('global_all');

    return aggIds;
  }

  updateAllRelevantAggregations(userId: string, level: LevelCompletion): void {
    const today = getTodayDateString();
    
    // Create unique key for this level completion using timestamp
    const timestamp = level.timestamp instanceof Date ? level.timestamp.getTime() : new Date(level.timestamp).getTime();
    const levelKey = `${userId}_${level.levelId}_${timestamp}`;
    
    // Check if already processed
    if (this.processedLevels.has(levelKey)) {
      console.warn(`⚠️ DUPLICATE: Level ${level.levelId} already processed, skipping aggregation update`);
      return;
    }
    
    // Mark as processed
    this.processedLevels.add(levelKey);
    
    console.log(`\n========================================`);
    console.log(`🔄 AGGREGATION UPDATE: Processing level completion`);
    console.log(`========================================`);
    console.log(`Level ID: ${level.levelId}`);
    console.log(`Level Key: ${levelKey}`);
    console.log(`Mode: ${level.mode}`);
    console.log(`Language: ${level.language}`);
    console.log(`KeyboardId: ${level.keyboardId}`);
    console.log(`ErrorMode: ${level.errorMode}`);
    console.log(`Today: ${today}`);
    
    const aggIds: string[] = [];
    
    // 1. Daily aggregation (date only)
    aggIds.push(`daily_${today}`);
    
    // 1b. Daily + error mode (for filtering daily activity by error mode)
    if (level.errorMode) {
      aggIds.push(`combined_daily_${today}_${level.errorMode}`);
    }
    
    // 2. Single-filter aggregations
    aggIds.push(`mode_${level.mode}`);
    aggIds.push(`language_${level.language}`);
    aggIds.push(`keyboard_${level.keyboardId}`);
    
    // 2b. Error mode single-filter aggregation (for ALL modes, not just just_play)
    if (level.errorMode) {
      aggIds.push(`combined_errorMode_${level.errorMode}`);
      
      // For just_play mode: also update the legacy mode_just_play_errorMode aggregation
      if (level.mode === 'just_play') {
        aggIds.push(`mode_just_play_${level.errorMode}`);
      }
    }
    
    // 3. Two-filter combinations
    aggIds.push(`combined_${level.mode}_${level.language}`); // mode + language
    aggIds.push(`combined_${level.mode}_${level.keyboardId}`); // mode + keyboard
    aggIds.push(`combined_${level.language}_${level.keyboardId}`); // language + keyboard
    
    // 3b. Daily + filter combinations (for ALL modes, not just error mode)
    aggIds.push(`combined_daily_${today}_${level.mode}`); // daily + mode (for filtered daily activity)
    aggIds.push(`combined_daily_${today}_${level.language}`); // daily + language
    aggIds.push(`combined_daily_${today}_${level.keyboardId}`); // daily + keyboard
    
    // 3c. Add error mode two-filter combinations (if error mode exists)
    if (level.errorMode) {
      aggIds.push(`combined_${level.mode}_${level.errorMode}`); // mode + errorMode
      aggIds.push(`combined_${level.language}_${level.errorMode}`); // language + errorMode
      aggIds.push(`combined_${level.keyboardId}_${level.errorMode}`); // keyboard + errorMode
    }
    
    // 4. Three-filter combinations
    aggIds.push(`combined_${level.mode}_${level.language}_${level.keyboardId}`); // mode + language + keyboard
    
    // 4b. Daily three-filter combinations (for ALL modes)
    aggIds.push(`combined_daily_${today}_${level.mode}_${level.language}`); // daily + mode + language
    aggIds.push(`combined_daily_${today}_${level.mode}_${level.keyboardId}`); // daily + mode + keyboard
    aggIds.push(`combined_daily_${today}_${level.language}_${level.keyboardId}`); // daily + language + keyboard
    
    // 4c. Error mode three-filter combinations (if error mode exists)
    if (level.errorMode) {
      aggIds.push(`combined_${level.mode}_${level.language}_${level.errorMode}`); // mode + language + errorMode
      aggIds.push(`combined_${level.mode}_${level.keyboardId}_${level.errorMode}`); // mode + keyboard + errorMode
      aggIds.push(`combined_${level.language}_${level.keyboardId}_${level.errorMode}`); // language + keyboard + errorMode
      aggIds.push(`combined_daily_${today}_${level.mode}_${level.errorMode}`); // daily + mode + errorMode
      aggIds.push(`combined_daily_${today}_${level.language}_${level.errorMode}`); // daily + language + errorMode
      aggIds.push(`combined_daily_${today}_${level.keyboardId}_${level.errorMode}`); // daily + keyboard + errorMode
    }
    
    // 5. Four-filter combination (all filters including error mode)
    if (level.errorMode) {
      aggIds.push(`combined_${level.mode}_${level.language}_${level.keyboardId}_${level.errorMode}`);
      aggIds.push(`combined_daily_${today}_${level.mode}_${level.language}_${level.errorMode}`); // daily + mode + language + errorMode
    }
    
    // 6. Session aggregation (special case for tracking course/just_play progress)
    if (level.mode === 'just_play') {
      // For just_play, session includes error mode (session per category + keyboard + errorMode)
      if (level.justPlayCategory && level.errorMode) {
        aggIds.push(`session_justplay_${level.justPlayCategory}_${level.keyboardId}_${level.errorMode}`);
      }
      // DO NOT create course session aggregations for just_play mode
    } else if (level.mode === 'courses') {
      // For courses ONLY: mixed-keyboard or single-keyboard session
      const mixedSessionAggId = `session_${level.language}_mixed`;
      const mixedAgg = this.getAggregation(userId, mixedSessionAggId);
      
      if (mixedAgg && mixedAgg.totalLevels > 0) {
        // Mixed keyboard course exists - update ONLY the mixed session
        aggIds.push(mixedSessionAggId);
        console.log(`  🔀 Updating mixed session: ${mixedSessionAggId} (course is in mixed mode)`);
      } else {
        // Single keyboard course - update the keyboard-specific session
        aggIds.push(`session_${level.language}_${level.keyboardId}`);
      }
    }
    
    // 6. Global aggregation (no filters)
    aggIds.push('global_all');
    
    const startTime = Date.now();
    
    aggIds.forEach(aggId => {
      this.updateAggregation(userId, aggId, level);
    });
    
    const duration = Date.now() - startTime;
    console.log(`✅ Updated ${aggIds.length} aggregations in ${duration}ms`);
    console.log(`  Updated aggIds:`, aggIds);
    
    // Dispatch event for UI updates
    window.dispatchEvent(new CustomEvent('aggregationsUpdated', {
      detail: { userId, aggIds }
    }));
    window.dispatchEvent(new CustomEvent('progressUpdated'));

    // Live aggregations -> Firestore every LEVELS_PER_FLUSH completions.
    // Bounded write cost (replaces the old 24h "only on logout" sync for the
    // dashboard-rendered tier; profile-page stats still flow through the lazy
    // batch). Fire-and-forget; failures retry on the next trigger.
    this.maybeTriggerLiveFlush(userId);
  }
  
  /**
   * Get all daily aggregations for date range (for heatmap)
   */
  getDailyAggregations(userId: string, startDate: string, endDate: string): Aggregation[] {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const aggregations: Aggregation[] = [];
    
    const current = new Date(start);
    while (current <= end) {
      // Use local timezone for date string
      const dateStr = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
      const aggId = `daily_${dateStr}`;
      const agg = this.getAggregation(userId, aggId);
      
      // Filter out deleted aggregations (soft delete)
      if (agg && !(agg as any).deleted) {
        aggregations.push(agg);
      }
      
      current.setDate(current.getDate() + 1);
    }
    
    return aggregations;
  }
  
  /**
   * Iterate the in-memory cache for entries belonging to `userId` whose aggId
   * starts with `aggIdPrefix`. Skips soft-deleted aggregations.
   */
  private collectFromCache(userId: string, aggIdPrefix: string): Aggregation[] {
    const fullPrefix = `${userId}_${aggIdPrefix}`;
    const out: Aggregation[] = [];
    for (const [key, agg] of this.cache) {
      if (!key.startsWith(fullPrefix)) continue;
      if ((agg as any).deleted) continue;
      out.push(agg);
    }
    return out;
  }

  /**
   * Get all session aggregations for a user
   */
  async getAllSessionAggregations(userId: string): Promise<Aggregation[]> {
    return this.collectFromCache(userId, 'session_');
  }

  /**
   * Get all combined (mode + language) aggregations for a user
   */
  async getAllCombinedAggregations(userId: string): Promise<Aggregation[]> {
    return this.collectFromCache(userId, 'combined_');
  }

  /**
   * Get all historical aggregations (keyboard, language, mode, global).
   * These contain cumulative historical data (dailyBreakdown, keyMetrics).
   */
  async getAllHistoricalAggregations(userId: string): Promise<Aggregation[]> {
    const out: Aggregation[] = [];
    const userPrefix = `${userId}_`;
    for (const [key, agg] of this.cache) {
      if (!key.startsWith(userPrefix)) continue;
      if ((agg as any).deleted) continue;
      const aggId = key.slice(userPrefix.length);
      if (
        aggId.startsWith('keyboard_') ||
        aggId.startsWith('language_') ||
        aggId.startsWith('mode_') ||
        aggId.startsWith('global_')
      ) {
        out.push(agg);
      }
    }
    return out;
  }

  /**
   * Get all aggregations matching a prefix (e.g., 'combined_', 'session_')
   */
  getAllAggregationsByPrefix(userId: string, aggIdPrefix: string): Aggregation[] {
    return this.collectFromCache(userId, aggIdPrefix);
  }

  /**
   * Get all mode aggregations for a user
   */
  async getAllModeAggregations(userId: string): Promise<Aggregation[]> {
    return this.collectFromCache(userId, 'mode_');
  }
  
  /**
   * Recalculate aggregations affected by a specific session deletion
   * More efficient than recalculating everything
   */
  async recalculateAggregationsForSession(
    userId: string, 
    language: string, 
    keyboardId: string, 
    mode: string = 'courses'
  ): Promise<void> {
    console.log(`🔄 Recalculating aggregations affected by session ${language}/${keyboardId}...`);
    
    try {
      // Get all remaining (non-deleted) three-filter aggregations
      const threeFilterAggs = this.getAllAggregationsByPrefix(userId, 'combined_')
        .filter(agg => {
          return agg.filterValues?.mode && 
                 agg.filterValues?.language && 
                 agg.filterValues?.keyboardId;
        });
      
      // Get all remaining (non-deleted) session aggregations
      const sessionAggs = await this.getAllSessionAggregations(userId);
      
      console.log(`  📊 Found ${threeFilterAggs.length} three-filter aggs, ${sessionAggs.length} session aggs remaining`);
      
      // If NO aggregations remain (user deleted all their data), clear global_all
      if (threeFilterAggs.length === 0 && sessionAggs.length === 0) {
        console.log(`  ⚠️  No remaining aggregations - creating empty global_all`);
        this.rebuildAggregationFromCombined(
          userId, 'global_all', 'global', {}, []
        );
        
        // Dispatch events immediately
        window.dispatchEvent(new CustomEvent('aggregationsUpdated', { 
          detail: { userId, sessionDeleted: true, allDataDeleted: true } 
        }));
        window.dispatchEvent(new CustomEvent('progressUpdated'));
        
        console.log(`✅ Cleared global_all (no data remaining)`);
        return;
      }
      
      // === Rebuild affected two-filter combinations ===
      
      // 1. mode + language (e.g., combined_courses_python)
      const modeLangAggId = `combined_${mode}_${language}`;
      const modeLangSources = threeFilterAggs.filter(
        agg => agg.filterValues?.mode === mode && agg.filterValues?.language === language
      );
      if (modeLangSources.length > 0) {
        this.rebuildAggregationFromCombined(
          userId, modeLangAggId, 'combined',
          { mode: mode as any, language },
          modeLangSources
        );
      }
      
      // 2. mode + keyboard (e.g., combined_courses_qwerty)
      const modeKbAggId = `combined_${mode}_${keyboardId}`;
      const modeKbSources = threeFilterAggs.filter(
        agg => agg.filterValues?.mode === mode && agg.filterValues?.keyboardId === keyboardId
      );
      if (modeKbSources.length > 0) {
        this.rebuildAggregationFromCombined(
          userId, modeKbAggId, 'combined',
          { mode: mode as any, keyboardId },
          modeKbSources
        );
      }
      
      // 3. language + keyboard (e.g., combined_python_qwerty)
      const langKbAggId = `combined_${language}_${keyboardId}`;
      const langKbSources = [...threeFilterAggs, ...sessionAggs].filter(
        agg => agg.filterValues?.language === language && agg.filterValues?.keyboardId === keyboardId
      );
      if (langKbSources.length > 0) {
        this.rebuildAggregationFromCombined(
          userId, langKbAggId, 'combined',
          { language, keyboardId },
          langKbSources
        );
      }
      
      // === Rebuild affected single-filter aggregations ===
      
      // 1. Language
      const langAggId = `language_${language}`;
      const langSources = threeFilterAggs.filter(agg => agg.filterValues?.language === language);
      if (langSources.length > 0) {
        this.rebuildAggregationFromCombined(
          userId, langAggId, 'language',
          { language },
          langSources
        );
      }
      
      // 2. Mode
      const modeAggId = `mode_${mode}`;
      const modeSources = threeFilterAggs.filter(agg => agg.filterValues?.mode === mode);
      if (modeSources.length > 0) {
        this.rebuildAggregationFromCombined(
          userId, modeAggId, 'mode',
          { mode: mode as any },
          modeSources
        );
      }
      
      // 3. Keyboard
      const kbAggId = `keyboard_${keyboardId}`;
      const kbSources = [...threeFilterAggs, ...sessionAggs].filter(
        agg => agg.filterValues?.keyboardId === keyboardId
      );
      if (kbSources.length > 0) {
        this.rebuildAggregationFromCombined(
          userId, kbAggId, 'keyboard',
          { keyboardId },
          kbSources
        );
      }
      
      // === Rebuild global aggregation ===
      this.rebuildAggregationFromCombined(
        userId, 'global_all', 'global', {},
        [...threeFilterAggs, ...sessionAggs]
      );
      
      console.log(`✅ Recalculated aggregations affected by session ${language}/${keyboardId}`);
      
      // Dispatch events
      window.dispatchEvent(new CustomEvent('aggregationsUpdated', { 
        detail: { userId, sessionDeleted: true } 
      }));
      window.dispatchEvent(new CustomEvent('progressUpdated'));
      
    } catch (error) {
      console.error('Error recalculating session aggregations:', error);
      throw error;
    }
  }

  /**
   * Recalculate aggregations affected by a keyboard deletion
   * Rebuilds all keyboard-related aggregations
   */
  async recalculateAggregationsForKeyboard(userId: string, keyboardId: string): Promise<void> {
    console.log(`🔄 Recalculating aggregations affected by keyboard ${keyboardId} deletion...`);
    
    try {
      // Get all three-filter aggregations
      const threeFilterAggs = this.getAllAggregationsByPrefix(userId, 'combined_')
        .filter(agg => {
          return agg.filterValues?.mode && 
                 agg.filterValues?.language && 
                 agg.filterValues?.keyboardId &&
                 agg.filterValues?.keyboardId !== keyboardId; // Exclude deleted keyboard
        });
      
      const sessionAggs = (await this.getAllSessionAggregations(userId))
        .filter(agg => agg.filterValues?.keyboardId !== keyboardId); // Exclude deleted keyboard
      
      // Get unique modes and languages that were using this keyboard
      const affectedModes = new Set<string>();
      const affectedLanguages = new Set<string>();
      
      threeFilterAggs.forEach(agg => {
        if (agg.filterValues?.mode) affectedModes.add(agg.filterValues.mode);
        if (agg.filterValues?.language) affectedLanguages.add(agg.filterValues.language);
      });
      
      sessionAggs.forEach(agg => {
        if (agg.filterValues?.language) affectedLanguages.add(agg.filterValues.language);
      });
      
      console.log(`  Affected modes: ${Array.from(affectedModes).join(', ')}`);
      console.log(`  Affected languages: ${Array.from(affectedLanguages).join(', ')}`);
      
      // Rebuild keyboard aggregation (should be empty or removed)
      const kbAggId = `keyboard_${keyboardId}`;
      await this.markAggregationAsDeleted(userId, kbAggId);
      
      // Rebuild all mode+keyboard and language+keyboard combinations
      for (const mode of affectedModes) {
        const modeKbAggId = `combined_${mode}_${keyboardId}`;
        await this.markAggregationAsDeleted(userId, modeKbAggId);
      }
      
      for (const language of affectedLanguages) {
        const langKbAggId = `combined_${language}_${keyboardId}`;
        await this.markAggregationAsDeleted(userId, langKbAggId);
        
        // Rebuild three-filter combinations for this keyboard
        for (const mode of affectedModes) {
          const threeFilterAggId = `combined_${mode}_${language}_${keyboardId}`;
          await this.markAggregationAsDeleted(userId, threeFilterAggId);
        }
      }
      
      // Recalculate all cross-session aggregations to exclude the deleted keyboard
      await this.recalculateCrossSessionAggregations(userId);
      
      console.log(`✅ Recalculated aggregations after keyboard ${keyboardId} deletion`);
      
    } catch (error) {
      console.error('Error recalculating keyboard aggregations:', error);
      throw error;
    }
  }

  /**
   * Recalculate ALL aggregations from session and combined aggregations.
   * This rebuilds:
   * - Single-filter aggregations (language, mode, keyboard)
   * - Two-filter combinations (mode+language, mode+keyboard, language+keyboard)
   * - Three-filter combinations (mode+language+keyboard)
   * - Global aggregation
   * 
   * Call this after deleting sessions to ensure aggregations are accurate.
   */
  async recalculateCrossSessionAggregations(userId: string): Promise<void> {
    console.log('🔄 Recalculating ALL aggregations from non-deleted data...');
    
    // Get all non-deleted three-filter combined aggregations (mode+language+keyboard)
    const threeFilterAggs = this.getAllAggregationsByPrefix(userId, 'combined_')
      .filter(agg => {
        // Three-filter: has all three filter values
        return agg.filterValues?.mode && 
               agg.filterValues?.language && 
               agg.filterValues?.keyboardId;
      });
    console.log(`  Found ${threeFilterAggs.length} three-filter aggregations (mode+language+keyboard)`);
    
    // Get all non-deleted session aggregations
    const sessionAggs = await this.getAllSessionAggregations(userId);
    console.log(`  Found ${sessionAggs.length} non-deleted session aggregations`);
    
    if (threeFilterAggs.length === 0 && sessionAggs.length === 0) {
      console.log('  No data to recalculate from');
      return;
    }
    
    // Collect unique values for each filter dimension
    const languages = new Set<string>();
    const modes = new Set<string>();
    const keyboards = new Set<string>();
    
    threeFilterAggs.forEach(agg => {
      if (agg.filterValues?.language) languages.add(agg.filterValues.language);
      if (agg.filterValues?.mode) modes.add(agg.filterValues.mode);
      if (agg.filterValues?.keyboardId) keyboards.add(agg.filterValues.keyboardId);
    });
    
    sessionAggs.forEach(agg => {
      if (agg.filterValues?.language) languages.add(agg.filterValues.language);
      if (agg.filterValues?.keyboardId) keyboards.add(agg.filterValues.keyboardId);
    });
    
    console.log(`  Languages: ${Array.from(languages).join(', ')}`);
    console.log(`  Modes: ${Array.from(modes).join(', ')}`);
    console.log(`  Keyboards: ${Array.from(keyboards).join(', ')}`);
    
    // === 1. Single-filter aggregations ===
    console.log('\n  📊 Rebuilding single-filter aggregations...');
    
    for (const language of languages) {
      const aggId = `language_${language}`;
      const matchingAggs = threeFilterAggs.filter(agg => agg.filterValues?.language === language);
      this.rebuildAggregationFromCombined(userId, aggId, 'language', { language }, matchingAggs);
    }
    
    for (const mode of modes) {
      const aggId = `mode_${mode}`;
      const matchingAggs = threeFilterAggs.filter(agg => agg.filterValues?.mode === mode);
      this.rebuildAggregationFromCombined(userId, aggId, 'mode', { mode: mode as GameplayMode }, matchingAggs);
    }
    
    for (const keyboard of keyboards) {
      const aggId = `keyboard_${keyboard}`;
      const matchingAggs = [...threeFilterAggs, ...sessionAggs].filter(
        agg => agg.filterValues?.keyboardId === keyboard
      );
      this.rebuildAggregationFromCombined(userId, aggId, 'keyboard', { keyboardId: keyboard }, matchingAggs);
    }
    
    // === 2. Two-filter combinations ===
    console.log('\n  📊 Rebuilding two-filter aggregations...');
    
    // mode + language
    for (const mode of modes) {
      for (const language of languages) {
        const aggId = `combined_${mode}_${language}`;
        const matchingAggs = threeFilterAggs.filter(
          agg => agg.filterValues?.mode === mode && agg.filterValues?.language === language
        );
        if (matchingAggs.length > 0) {
          this.rebuildAggregationFromCombined(
            userId, aggId, 'combined', 
            { mode: mode as GameplayMode, language }, 
            matchingAggs
          );
        }
      }
    }
    
    // mode + keyboard
    for (const mode of modes) {
      for (const keyboard of keyboards) {
        const aggId = `combined_${mode}_${keyboard}`;
        const matchingAggs = threeFilterAggs.filter(
          agg => agg.filterValues?.mode === mode && agg.filterValues?.keyboardId === keyboard
        );
        if (matchingAggs.length > 0) {
          this.rebuildAggregationFromCombined(
            userId, aggId, 'combined', 
            { mode: mode as GameplayMode, keyboardId: keyboard }, 
            matchingAggs
          );
        }
      }
    }
    
    // language + keyboard (from session aggregations)
    for (const language of languages) {
      for (const keyboard of keyboards) {
        const aggId = `combined_${language}_${keyboard}`;
        const matchingAggs = [...threeFilterAggs, ...sessionAggs].filter(
          agg => agg.filterValues?.language === language && agg.filterValues?.keyboardId === keyboard
        );
        if (matchingAggs.length > 0) {
          this.rebuildAggregationFromCombined(
            userId, aggId, 'combined', 
            { language, keyboardId: keyboard }, 
            matchingAggs
          );
        }
      }
    }
    
    // === 3. Global aggregation ===
    console.log('\n  📊 Rebuilding global aggregation...');
    this.rebuildAggregationFromCombined(
      userId, 'global_all', 'global', {}, 
      [...threeFilterAggs, ...sessionAggs]
    );
    
    console.log('\n✅ All aggregations recalculated successfully');
    
    // Dispatch events
    window.dispatchEvent(new CustomEvent('aggregationsUpdated', { 
      detail: { userId, rebuilt: true } 
    }));
    window.dispatchEvent(new CustomEvent('progressUpdated'));
  }
  
  /**
   * Rebuild a single aggregation from multiple combined aggregations
   */
  private rebuildAggregationFromCombined(
    userId: string,
    aggId: string,
    type: AggregationType,
    filterValues: FilterValues,
    sourceAggs: Aggregation[]
  ): void {
    console.log(`  🔨 Rebuilding ${aggId} from ${sourceAggs.length} source aggregations`);
    
    // Create a fresh aggregation
    const now = new Date();
    const newAgg: Aggregation = {
      id: aggId,
      type,
      filterValues,
      lastUpdated: now,
      localTimestamp: now,
      
      totalLevels: 0,
      totalKeystrokes: 0,
      totalErrors: 0,
      totalTime: 0,
      
      avgWPM: 0,
      avgTPM: 0,
      avgAccuracy: 0,
      
      bestWPM: 0,
      bestTPM: 0,
      bestAccuracy: 0,
      
      keyMetrics: {},
      digraphs: {},
      topMistakes: [],
      dailyBreakdown: [],
    };
    
    // Aggregate data from all source aggregations
    const dailyMap = new Map<string, any>();
    
    sourceAggs.forEach(sourceAgg => {
      // Sum totals
      newAgg.totalLevels += sourceAgg.totalLevels || 0;
      newAgg.totalKeystrokes += sourceAgg.totalKeystrokes || 0;
      newAgg.totalErrors += sourceAgg.totalErrors || 0;
      newAgg.totalTime += sourceAgg.totalTime || 0;
      
      // Track best metrics
      if (sourceAgg.bestWPM > newAgg.bestWPM) newAgg.bestWPM = sourceAgg.bestWPM;
      if (sourceAgg.bestTPM > newAgg.bestTPM) newAgg.bestTPM = sourceAgg.bestTPM;
      if (sourceAgg.bestAccuracy > newAgg.bestAccuracy) newAgg.bestAccuracy = sourceAgg.bestAccuracy;
      
      // Merge key metrics
      Object.entries(sourceAgg.keyMetrics || {}).forEach(([key, metrics]) => {
        if (!newAgg.keyMetrics[key]) {
          newAgg.keyMetrics[key] = {
            totalAttempts: metrics.totalAttempts,
            totalErrors: metrics.totalErrors,
            totalTime: metrics.totalTime,
            avgSpeed: 0,  // Will be computed on read
            errorRate: 0, // Will be computed on read
          };
        } else {
          const existing = newAgg.keyMetrics[key];
          existing.totalAttempts += metrics.totalAttempts;
          existing.totalErrors += metrics.totalErrors;
          existing.totalTime += metrics.totalTime;
        }
      });
      
      // Merge digraphs
      Object.entries(sourceAgg.digraphs || {}).forEach(([pair, metrics]) => {
        if (!newAgg.digraphs[pair]) {
          newAgg.digraphs[pair] = {
            count: metrics.count,
            totalTime: metrics.totalTime,
            avgTime: 0,  // Will be computed on read
          };
        } else {
          const existing = newAgg.digraphs[pair];
          existing.count += metrics.count;
          existing.totalTime += metrics.totalTime;
        }
      });
      
      // Merge daily breakdown
      if (sourceAgg.dailyBreakdown) {
        sourceAgg.dailyBreakdown.forEach(day => {
          const existing = dailyMap.get(day.date);
          if (existing) {
            const oldCount = existing.levelCount;
            const newCount = oldCount + day.levelCount;
            existing.levelCount = newCount;
            existing.sessions += day.sessions || 1;
            
            // Weighted averages
            if (day.avgWpm) {
              existing.avgWpm = ((existing.avgWpm || 0) * oldCount + day.avgWpm * day.levelCount) / newCount;
            }
            if (day.avgTpm) {
              existing.avgTpm = ((existing.avgTpm || 0) * oldCount + day.avgTpm * day.levelCount) / newCount;
            }
            if (day.avgAccuracy !== undefined) {
              existing.avgAccuracy = ((existing.avgAccuracy || 0) * oldCount + day.avgAccuracy * day.levelCount) / newCount;
            }
          } else {
            dailyMap.set(day.date, { ...day });
          }
        });
      }
    });
    
    // Merge topMistakes (confusions) from all source aggregations
    const mistakesMap = new Map<string, { expected: string; typed: string; count: number }>();
    sourceAggs.forEach(sourceAgg => {
      if (sourceAgg.topMistakes) {
        sourceAgg.topMistakes.forEach((mistake: any) => {
          const key = `${mistake.expected}→${mistake.typed}`;
          const existing = mistakesMap.get(key);
          if (existing) {
            existing.count += mistake.count || 0;
          } else {
            mistakesMap.set(key, {
              expected: mistake.expected,
              typed: mistake.typed,
              count: mistake.count || 0,
            });
          }
        });
      }
    });
    
    // Convert topMistakes map to sorted array (top 50 most common)
    newAgg.topMistakes = Array.from(mistakesMap.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 50);
    
    // Calculate averages
    if (newAgg.totalLevels > 0) {
      newAgg.avgWPM = sourceAggs.reduce((sum, agg) => sum + (agg.avgWPM || 0) * (agg.totalLevels || 0), 0) / newAgg.totalLevels;
      newAgg.avgTPM = sourceAggs.reduce((sum, agg) => sum + (agg.avgTPM || 0) * (agg.totalLevels || 0), 0) / newAgg.totalLevels;
      newAgg.avgAccuracy = sourceAggs.reduce((sum, agg) => sum + (agg.avgAccuracy || 0) * (agg.totalLevels || 0), 0) / newAgg.totalLevels;
    }
    
    // Convert dailyMap to array
    newAgg.dailyBreakdown = Array.from(dailyMap.values()).sort((a, b) => a.date.localeCompare(b.date));
    
    // Save the rebuilt aggregation
    this.saveAggregation(userId, newAgg);
    
    console.log(`    ✅ ${aggId}: ${newAgg.totalLevels} levels, ${newAgg.dailyBreakdown?.length || 0} days, ${Object.keys(newAgg.keyMetrics).length} keys, ${newAgg.topMistakes.length} mistakes`);
  }

  /**
   * Rebuild all aggregations from level completions
   * Clears all aggregations and recalculates from scratch
   */
  async rebuildAllAggregations(userId: string): Promise<void> {
    console.log('🔄 Rebuilding ALL aggregations from level completions...');
    
    try {
      // Step 1: Get all non-deleted level completions via the cache-backed
      // LevelCompletionService (delegates to IDB, no longer scans localStorage).
      const { levelCompletionService } = await import('./LevelCompletionService');
      await levelCompletionService.hydrate(userId);
      const completions = await levelCompletionService.getAllCompletions(userId);
      console.log(`  Found ${completions.length} non-deleted level completions`);
      
      // Step 2: Clear all aggregations
      this.clearAllAggregations(userId);
      console.log('  Cleared all existing aggregations');
      
      // Step 3: Re-add each level completion to rebuild aggregations
      let processedCount = 0;
      let skippedCount = 0;
      for (const completion of completions) {
        const aggIds = this.buildAggIdsForLevel(completion);

        const hasKeyboard = !!completion.keyboardId;
        const hasLanguage = !!completion.language;
        const hasMode = !!completion.mode;
        if (!hasKeyboard || !hasLanguage || !hasMode) {
          skippedCount++;
          console.warn(
            `  ⚠️ Completion ${completion.levelId} has missing fields (keyboardId="${completion.keyboardId}" language="${completion.language}" mode="${completion.mode}"); some aggregations will be skipped.`,
          );
        }

        aggIds.forEach(aggId => {
          this.updateAggregation(userId, aggId, completion);
        });

        processedCount++;

        if (processedCount % 10 === 0) {
          console.log(`  Processed ${processedCount}/${completions.length} completions...`);
        }
      }

      if (skippedCount > 0) {
        console.warn(`⚠️ ${skippedCount} completion(s) had missing keyboardId/language/mode and were partially skipped during rebuild.`);
      }
      
      console.log(`✅ Successfully rebuilt all aggregations from ${completions.length} level completions`);
      
      // Dispatch event to refresh UI
      window.dispatchEvent(new CustomEvent('aggregationsUpdated', { 
        detail: { userId, rebuilt: true } 
      }));
      window.dispatchEvent(new CustomEvent('progressUpdated'));
      
    } catch (error) {
      console.error('❌ Error rebuilding aggregations:', error);
      throw error;
    }
  }

  /**
   * Sync aggregation to Firestore for persistence across browsers
   * This is async and non-blocking - localStorage is the source of truth
   * @deprecated Use addToSyncQueue + debouncedBatchSync instead for efficiency
   */
  private async syncAggregationToFirestore(userId: string, agg: Aggregation): Promise<void> {
    try {
      // Use the Firestore service to save the aggregation
      // We'll use the activity aggregation structure which is compatible
      await firestoreService.saveAggregation(userId, agg);
    } catch (error: any) {
      // Silently fail if offline or permissions issue
      if (error?.code === 'unavailable' || error?.code === 'permission-denied') {
        // User is offline or doesn't have write permissions
        return;
      }
      throw error;
    }
  }

  /**
   * Add aggregation to sync queue (batched)
   */
  private addToSyncQueue(userId: string, aggId: string): void {
    this.currentUserId = userId;
    this.syncQueue.add(aggId);
  }
  
  /**
   * Debounced batch sync to Firestore
   */
  private debouncedBatchSync(userId: string): void {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
    }
    
    this.syncTimer = setTimeout(() => {
      this.flushSyncQueue(userId);
    }, this.SYNC_DEBOUNCE_MS);
  }
  
  /**
   * Flush sync queue - upload all pending aggregations in a batch
   */
  private async flushSyncQueue(userId: string): Promise<void> {
    if (this.syncQueue.size === 0) return;
    
    const aggIds = Array.from(this.syncQueue);
    this.syncQueue.clear();
    
    console.log(`☁️ Batch syncing ${aggIds.length} aggregations to Firestore...`);
    const startTime = Date.now();
    
    try {
      // Load aggregations from the in-memory cache (silently skip missing ones).
      const aggregations = aggIds
        .map(aggId => this.cache.get(cacheKey(userId, aggId)))
        .filter((agg): agg is Aggregation => agg !== undefined);
      
      if (aggregations.length === 0) {
        console.log(`  ⏭️ No aggregations to sync (all were missing or invalid)`);
        return;
      }
      
      // Batch upload using existing batchSaveAggregations
      await firestoreService.batchSaveAggregations(userId, aggregations);
      
      const duration = Date.now() - startTime;
      const skipped = aggIds.length - aggregations.length;
      console.log(`✅ Batch synced ${aggregations.length}/${aggIds.length} aggregations in ${duration}ms${skipped > 0 ? ` (${skipped} skipped)` : ''}`);
    } catch (error) {
      console.error('❌ Batch sync failed:', error);
      // Re-add to queue for retry
      aggIds.forEach(id => this.syncQueue.add(id));
    }
  }

  /**
   * Flush pending syncs immediately (called on logout or when needed)
   * 
   * NOTE: This method is now a no-op as real-time aggregation syncing has been
   * disabled for optimization. Aggregations sync only via DailyAggregationSyncService.
   * Kept for backward compatibility.
   * 
   * @deprecated Aggregations now sync daily or on logout via DailyAggregationSyncService
   */
  async flushPendingSyncs(userId: string): Promise<void> {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
    // No-op: syncQueue will always be empty since updateAggregation no longer adds to it
    await this.flushSyncQueue(userId);
  }

  /**
   * Sync all aggregations FROM Firestore to localStorage
   * Called when user logs in on a new browser/domain where localStorage is empty
   * This ensures user data persists across different access methods
   */
  async syncFromFirestore(userId: string): Promise<{ synced: number; skipped: number }> {
    console.log(`🔄 Syncing aggregations from Firestore for user ${userId}...`);
    await this.hydrate(userId);
    try {
      // Get all aggregations from Firestore
      const allAggregations = await firestoreService.getAllAggregations(userId);
      
      if (allAggregations.length === 0) {
        console.log('  ℹ️ No aggregations found in Firestore');
        return { synced: 0, skipped: 0 };
      }
      
      // Only sync essential aggregations to avoid quota issues:
      // - Session aggregations (session_*) for course progress display
      // - Global, mode, language, keyboard aggregations (single-filter) for historical data
      // - Combined three-filter and four-filter aggregations (mode+language+keyboard[+errorMode])
      // SKIP: Two-filter combined aggregations to save space (can be computed from three-filter)
      // SKIP: Daily keystroke aggregations which are huge and not needed
      const aggregations = allAggregations.filter(agg => {
        const id = agg.id || '';
        
        // Always include: session, global, mode, language, keyboard
        if (id.startsWith('session_') || 
            id.startsWith('global_') || 
            id.startsWith('language_') || 
            id.startsWith('keyboard_') || 
            id.startsWith('mode_')) {
          return true;
        }
        
        // For combined aggregations, only include three-filter and four-filter
        if (id.startsWith('combined_')) {
          // Skip daily combined aggregations (too many, not needed for historical view)
          if (id.includes('daily_')) {
            return false;
          }
          
          // Count underscore-separated parts (after 'combined_')
          const parts = id.replace('combined_', '').split('_');
          
          // Three-filter: mode_language_keyboard (3 parts, all non-kb)
          // Four-filter: mode_language_keyboard_errorMode (4+ parts)
          const hasKeyboard = parts.some(p => p.startsWith('kb_'));
          
          if (hasKeyboard) {
            // Has keyboard - this is likely a three-filter or four-filter agg
            // Count non-keyboard parts
            const nonKbParts = parts.filter(p => !p.startsWith('kb_'));
            // Should have at least 2 non-keyboard parts (mode, language) for three-filter
            return nonKbParts.length >= 2;
          } else {
            // No keyboard - skip two-filter combinations (mode+language, mode+errorMode, etc.)
            return false;
          }
        }
        
        return false;
      });
      
      console.log(`  📊 Smart filtering: ${allAggregations.length} total → ${aggregations.length} essential aggregations`);
      console.log(`  💡 Prioritizing: sessions, single-filters, three/four-filter combined (skipping two-filter and daily)`);
      
      let synced = 0;
      let skipped = 0;

      for (const agg of aggregations) {
        const ckey = cacheKey(userId, agg.id);
        const local = this.cache.get(ckey);
        const incoming = this.reviveAggregation(agg);

        let shouldWrite = false;
        if (!local) {
          shouldWrite = true;
        } else {
          const localTime = new Date(local.lastUpdated).getTime() || 0;
          const incomingTime = new Date(incoming.lastUpdated).getTime() || 0;
          shouldWrite = incomingTime > localTime;
        }

        if (shouldWrite) {
          this.cache.set(ckey, incoming);
          // Fire-and-forget IDB write; failure is a cache miss, not data loss.
          indexedDBService.putAggregation(userId, agg.id, incoming).catch(err => {
            console.warn(`IDB write failed during sync for ${agg.id}:`, err);
          });
          synced++;
        } else {
          skipped++;
        }
      }

      console.log(`✅ Sync complete: ${synced} synced, ${skipped} skipped (cache was newer)`);
      return { synced, skipped };
    } catch (error) {
      console.error('❌ Error syncing from Firestore:', error);
      return { synced: 0, skipped: 0 };
    }
  }

  /**
   * Check if user has any local aggregations
   */
  hasLocalAggregations(userId: string): boolean {
    const prefix = `${userId}_`;
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) return true;
    }
    return false;
  }

  /**
   * Clear all aggregations for a user (for testing/reset).
   * Drops cache entries, IDB rows, and any leftover legacy localStorage keys.
   */
  clearAllAggregations(userId: string): void {
    const prefix = `${userId}_`;
    for (const key of Array.from(this.cache.keys())) {
      if (key.startsWith(prefix)) this.cache.delete(key);
    }
    indexedDBService.deleteAllAggregationsForUser(userId).catch(err => {
      console.warn('IDB clear failed:', err);
    });
    // Drop any legacy localStorage entries that escaped migration.
    const lsPrefix = `${LEGACY_STORAGE_KEY_PREFIX}${userId}_`;
    try {
      const drop: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(lsPrefix)) drop.push(k);
      }
      drop.forEach(k => localStorage.removeItem(k));
    } catch {}
    this.hydrated.delete(userId);
    console.log(`🗑️ Cleared all aggregations for user ${userId}`);
  }

  /**
   * Push the "live" tier of aggregations to Firestore right now: the docs
   * actually rendered on the dashboard (`global_all`, today's `daily_<date>`,
   * any `session_*`). Other aggregations stay on the existing 24h batch.
   * Idempotent and safe to call repeatedly. Fire-and-forget at most call sites.
   */
  async flushLiveAggregations(userId: string): Promise<void> {
    if (this.liveFlushInFlight.has(userId)) return;
    this.liveFlushInFlight.add(userId);

    try {
      const today = getTodayDateString();
      const liveIds: string[] = ['global_all', `daily_${today}`];

      const sessionAggs = this.getAllAggregationsByPrefix(userId, 'session_');
      for (const agg of sessionAggs) {
        if (agg.id) liveIds.push(agg.id);
      }

      const aggsToWrite: Aggregation[] = [];
      for (const id of liveIds) {
        const agg = this.getAggregation(userId, id);
        if (agg) aggsToWrite.push(agg);
      }

      if (aggsToWrite.length === 0) return;

      await firestoreService.batchSaveAggregations(userId, aggsToWrite);
      console.log(`☁️ flushLiveAggregations: pushed ${aggsToWrite.length} live agg(s) for ${userId}`);
    } catch (error) {
      console.warn('flushLiveAggregations failed (will retry on next trigger):', error);
    } finally {
      this.liveFlushInFlight.delete(userId);
    }
  }

  /**
   * Increment the per-user level counter. When it crosses LEVELS_PER_FLUSH,
   * fire a fire-and-forget live-aggregation flush and reset. Called from
   * updateAllRelevantAggregations so every level completion path benefits.
   */
  private maybeTriggerLiveFlush(userId: string): void {
    const next = (this.levelsSinceFlush.get(userId) ?? 0) + 1;
    if (next >= this.LEVELS_PER_FLUSH) {
      this.levelsSinceFlush.set(userId, 0);
      void this.flushLiveAggregations(userId);
    } else {
      this.levelsSinceFlush.set(userId, next);
    }
  }

  /**
   * One-time backfill: push every locally-cached aggregation up to Firestore.
   * Fixes Firestore's stale partial state caused by the old "sync only on
   * logout, only every 24h" path. Idempotent via per-user localStorage marker.
   *
   * Writes are issued one-doc-at-a-time rather than via batchSaveAggregations
   * because the full set can easily exceed Firestore's 11.5 MB batch payload
   * cap (combined_/daily_ aggregations carry rich per-day breakdowns). Per-doc
   * writes are slower but resilient — one corrupt doc doesn't sink the rest,
   * and partial progress is durable across retries.
   */
  async backfillAggregationsToFirestore(userId: string): Promise<void> {
    const doneKey = `agg_backfill_done_${userId}`;
    if (localStorage.getItem(doneKey) === '1') return;
    await this.hydrate(userId);

    const local = this.getAllAggregationsByPrefix(userId, '');
    if (local.length === 0) {
      localStorage.setItem(doneKey, '1');
      return;
    }

    console.log(`📤 Backfilling ${local.length} aggregation(s) to Firestore (per-doc, ~${Math.ceil(local.length / 5)}s)...`);

    let succeeded = 0;
    const failures: Array<{ id: string; error: unknown }> = [];

    for (const agg of local) {
      try {
        await firestoreService.saveAggregation(userId, agg);
        succeeded++;
        if (succeeded % 25 === 0) {
          console.log(`  …${succeeded}/${local.length} aggregations pushed`);
        }
      } catch (error) {
        failures.push({ id: agg.id, error });
      }
    }

    if (failures.length === 0) {
      localStorage.setItem(doneKey, '1');
      console.log(`✅ Aggregation backfill complete (${succeeded} aggregations)`);
      return;
    }

    // Some docs failed. Log details so we can see what's hitting per-doc limits
    // (most likely: a single combined_*/daily_* doc over the 1 MiB cap).
    console.warn(
      `⚠️ Aggregation backfill: ${succeeded} succeeded, ${failures.length} failed. ` +
      `Will retry on next login. First failure:`,
      failures[0],
    );
    console.warn('  Failed aggregation IDs:', failures.map(f => f.id).slice(0, 20));
  }
}

export const aggregationService = new AggregationServiceClass();

